// ──────────────────────────────────────────────────────────────────────────
// Kenya2Go — curated public-site content (raw launch).
// Single source of truth for the marketing surface until destinations/stories
// move to the database or a CMS. `hue` drives the art-directed gradient art on
// cards (oklch hue angle). Keep copy authentic and specific.
// ──────────────────────────────────────────────────────────────────────────

export const destinations = [
  {
    slug: 'maasai-mara', name: 'Maasai Mara', region: 'Rift Valley · Southwest',
    tag: 'The Great Migration', hue: 30,
    blurb: 'Big cats, endless plains, and a million wildebeest on the move.',
    overview: [
      'The Mara is the Kenya of the imagination — golden grass to the horizon, acacia silhouettes, and the densest concentration of big cats in Africa. It is the northern reach of the Serengeti ecosystem, and from roughly July to October it hosts the climax of the Great Migration as the herds cross the Mara River.',
      'Beyond the wildlife, the reserve borders Maasai community land, and the people who have shared these plains with the animals for centuries remain central to any honest visit here.',
    ],
    highlights: ['Big Five game drives', 'Great Migration river crossings (Jul–Oct)', 'Hot-air balloon safari at dawn', 'Maasai community visits'],
    bestTime: 'July–October for the Migration; year-round for big cats.',
    experiences: ['3-Day Maasai Mara Safari', 'Mara Balloon Safari', 'Maasai Village Visit'],
  },
  {
    slug: 'amboseli', name: 'Amboseli', region: 'Southern Kenya',
    tag: 'Under Kilimanjaro', hue: 150,
    blurb: 'Great herds of elephant beneath the snows of Kilimanjaro.',
    overview: [
      'Amboseli trades the Mara’s big-cat drama for something more monumental: huge, free-ranging elephant herds framed by Mount Kilimanjaro rising across the border in Tanzania. The light here, especially at dawn and dusk, is some of the most photogenic in Africa.',
      'It is also Maasai country, and the open, short-grass plains make wildlife easy to spot against the mountain.',
    ],
    highlights: ['Iconic elephant-and-Kilimanjaro views', 'Big herds at close range', 'Birdlife on the swamps', 'Maasai culture'],
    bestTime: 'June–October & January–February (clear mountain views).',
    experiences: ['Amboseli Safari', 'Kilimanjaro-View Photo Drive'],
  },
  {
    slug: 'diani-south-coast', name: 'Diani & the South Coast', region: 'Indian Ocean Coast',
    tag: 'Swahili shores', hue: 200,
    blurb: 'White sand, warm Indian Ocean, and dhows under a low sun.',
    overview: [
      'Diani is the postcard of the Kenyan coast — a long ribbon of white sand, palm shade, and a reef that keeps the water calm and impossibly blue. It’s where a safari ends and the unwinding begins.',
      'The coast is also the heart of Swahili culture, a thousand-year-old blend of African, Arab and Indian worlds you can taste in the food and hear in the language.',
    ],
    highlights: ['Reef snorkelling & diving', 'Wasini Island & dolphins', 'Fresh Swahili seafood', 'Kite-surfing'],
    bestTime: 'October–March for the clearest water; dry and warm.',
    experiences: ['Diani Reef Snorkel', 'Wasini Dolphin Dhow', 'Swahili Cooking Afternoon'],
  },
  {
    slug: 'lamu', name: 'Lamu', region: 'North Coast',
    tag: 'Old town, no cars', hue: 48,
    blurb: 'Carved doors, sea breeze, and centuries of Swahili life.',
    overview: [
      'Lamu Old Town is a UNESCO World Heritage Site and the oldest continually inhabited Swahili settlement in East Africa. There are no cars — just donkeys, dhows, narrow coral-stone lanes and a pace of life unchanged for centuries.',
      'It is the most atmospheric place on the coast: a working town, not a museum, where you sleep in carved-plaster rooms and sail at sunset.',
    ],
    highlights: ['Car-free Old Town lanes', 'Sunset dhow sailing', 'Carved Swahili doors & rooftops', 'Lamu cuisine'],
    bestTime: 'Year-round; liveliest around the November Maulidi festival.',
    experiences: ['Lamu Dhow at Sunset', 'Old Town Heritage Walk'],
  },
  {
    slug: 'nairobi', name: 'Nairobi', region: 'Central Highlands',
    tag: 'The green city', hue: 135,
    blurb: 'Wildlife on the skyline, markets, music, and the real pulse.',
    overview: [
      'Most trips begin and end in Nairobi, and it deserves more than a transfer. It is the only capital on earth with a national park on its doorstep — lions and rhino against a city skyline — plus elephant orphans, a giraffe manor, and a restless creative energy in its music, food and markets.',
      'Spend a day here and Kenya stops being only about animals.',
    ],
    highlights: ['Nairobi National Park', 'Sheldrick Elephant Orphanage', 'Giraffe Centre', 'Maasai Market & food'],
    bestTime: 'Year-round.',
    experiences: ['Nairobi Food & Markets', 'Park, Elephants & Giraffes Day'],
  },
  {
    slug: 'samburu', name: 'Samburu', region: 'Northern Frontier',
    tag: 'The northern frontier', hue: 18,
    blurb: 'Rare species and proud cultures in raw, red country.',
    overview: [
      'North of Mount Kenya the land turns dry, red and dramatic. Samburu is home to species you won’t see in the south — Grevy’s zebra, reticulated giraffe, the gerenuk and Beisa oryx — the so-called "Samburu Special Five".',
      'The Samburu people, cousins of the Maasai, give the region a strong cultural identity.',
    ],
    highlights: ['Samburu Special Five', 'Elephants on the Ewaso Ng’iro river', 'Samburu culture', 'Fewer crowds'],
    bestTime: 'June–October & December–March.',
    experiences: ['Samburu Safari', 'Northern Frontier Cultural Day'],
  },
  {
    slug: 'tsavo', name: 'Tsavo', region: 'Southeast',
    tag: 'Red elephants, big skies', hue: 25,
    blurb: 'Kenya’s largest wilderness — vast, wild and dust-red.',
    overview: [
      'Tsavo East and West together form one of the largest protected areas on earth — raw, remote and far less crowded than the Mara. Its elephants are famously "red", dusted with the area’s ochre soil.',
      'Tsavo is for travellers who want space, scale and a sense of true wilderness, often on the way between Nairobi and the coast.',
    ],
    highlights: ['Red elephants', 'Mzima Springs', 'Vast open wilderness', 'Easy stop en route to the coast'],
    bestTime: 'June–October & January–February.',
    experiences: ['Tsavo Safari', 'Nairobi–Coast Safari Stopover'],
  },
  {
    slug: 'lake-nakuru', name: 'Lake Nakuru', region: 'Rift Valley',
    tag: 'Flamingos & rhino', hue: 330,
    blurb: 'A soda lake rimmed pink, and one of Kenya’s best rhino sanctuaries.',
    overview: [
      'Lake Nakuru is a shallow soda lake in the Rift Valley, often fringed with flamingos and pelicans, set in a fenced sanctuary that protects both black and white rhino.',
      'It’s compact, scenic and easy to combine with the Mara or Naivasha on a Rift Valley loop.',
    ],
    highlights: ['Black & white rhino', 'Flamingos & pelicans', 'Rothschild’s giraffe', 'Rift Valley viewpoints'],
    bestTime: 'Year-round; June–March for easiest game viewing.',
    experiences: ['Lake Nakuru Day Safari', 'Rift Valley Lakes Loop'],
  },
]

export const experiences = [
  { title: 'Maasai Mara Safari', kind: 'Safari',  days: '3 days',   from: 540, hue: 30,  blurb: 'Three days of big-cat country with a local guide.' },
  { title: 'Diani Reef Snorkel',  kind: 'Coast',   days: 'Half day', from: 65,  hue: 195, blurb: 'Drift the coral gardens off Diani’s warm reef.' },
  { title: 'Lamu Dhow at Sunset', kind: 'Culture', days: 'Evening',  from: 40,  hue: 45,  blurb: 'Sail the old harbour as the call to prayer drifts out.' },
  { title: 'Nairobi Food & Markets', kind: 'City', days: 'Day trip', from: 55,  hue: 140, blurb: 'Nyama choma, markets and the city’s real flavour.' },
  { title: 'Amboseli & Kilimanjaro', kind: 'Safari', days: '2 days', from: 380, hue: 150, blurb: 'Great elephant herds beneath the mountain.' },
  { title: 'Wasini Dolphin Dhow',  kind: 'Coast',  days: 'Full day', from: 95,  hue: 205, blurb: 'Dolphins, a marine park and a Swahili seafood lunch.' },
]

export const journal = [
  {
    slug: 'when-the-mara-moves', title: 'When the Mara moves', dek: 'Timing the Great Migration without the crowds.',
    category: 'Wildlife', readMins: 6, hue: 30, date: 'May 2026',
    body: [
      'Everyone wants the river crossing. Few realise the Migration is a year-round circle, and that the Mara’s best weeks are not always its busiest.',
      'We break down month by month where the herds are likely to be, when the crossings peak, and how to position yourself for the moment without sharing it with forty other vehicles.',
      'The short version: July to October for the crossings, but September often balances drama and space better than the August peak.',
    ],
  },
  {
    slug: 'eating-the-coast', title: 'Eating the coast', dek: 'A short, hungry guide to Swahili food.',
    category: 'Food', readMins: 5, hue: 200, date: 'April 2026',
    body: [
      'Coconut, tamarind, cardamom and the sea — Swahili cooking is the taste of a thousand years of trade across the Indian Ocean.',
      'From biryani and pilau to viazi karai and fresh-grilled fish, here’s what to order, where, and why the coast eats differently from the highlands.',
    ],
  },
  {
    slug: 'meeting-the-maasai-well', title: 'Meeting the Maasai, well', dek: 'How to visit a community as a guest, not a tourist.',
    category: 'Culture', readMins: 7, hue: 18, date: 'April 2026',
    body: [
      'A village visit can be a genuine exchange or an awkward performance. The difference is who runs it and how the money flows.',
      'We only work with community-led visits where the host sets the terms. Here’s what that looks like, what to ask before you book, and how to show up respectfully.',
    ],
  },
  {
    slug: 'first-safari-what-to-pack', title: 'Your first safari: what to actually pack', dek: 'Less than you think, in the right colours.',
    category: 'Practical', readMins: 4, hue: 70, date: 'March 2026',
    body: [
      'Neutral colours, layers for cold dawns and hot middays, a good camera and far fewer clothes than you’re imagining.',
      'A no-nonsense packing list for a Kenyan safari, plus the few things first-timers always forget.',
    ],
  },
]

export const journalBySlug = (slug) => journal.find((p) => p.slug === slug)
export const destinationBySlug = (slug) => destinations.find((d) => d.slug === slug)

// Tour categories (Kenya). Maps the Tour model enum keys to human labels for
// the public catalog. Keep keys in sync with app/models/tour.rb.
export const tourCategories = {
  safari:      'Safari',
  wildlife:    'Wildlife & nature',
  cultural:    'Cultural & heritage',
  beach_coast: 'Beach & coast',
  adventure:   'Adventure & active',
  city:        'City & day trips',
  day_trip:    'Day trips',
  other:       'Other',
}

// When-to-go: 12-month season map shared by Home + Seasonal.
export const months = [
  { m: 'Jan', s: 'dry' }, { m: 'Feb', s: 'dry' }, { m: 'Mar', s: 'green' }, { m: 'Apr', s: 'green' },
  { m: 'May', s: 'green' }, { m: 'Jun', s: 'peak' }, { m: 'Jul', s: 'peak' }, { m: 'Aug', s: 'peak' },
  { m: 'Sep', s: 'peak' }, { m: 'Oct', s: 'peak' }, { m: 'Nov', s: 'short' }, { m: 'Dec', s: 'dry' },
]
